import { PropsWithChildren, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { MdTripOrigin } from 'react-icons/md';
import { Divider, Listbox, ListboxItem, Popover, PopoverContent } from '@ds';
import {
  OMS_SHIFT_OPTION_DROPDOWN_LIST,
  OMS_ADMIN_DAY_COMBINE_INFO,
} from '@lp/features/Scheduler/SchedulerOMS/constants';
import {
  isAdminDayOption,
  getShiftOptionIconColor,
  isDualSlotOptionType,
} from '@lp/features/Scheduler/SchedulerOMS/utils';
import { OMSShift, OMSShiftSelectPayload } from '@/api/apis/resources/schedule';
import { useOMSShiftCellDropdown } from './hooks';
import { DualSlotDropdown } from './components';

type OMSShiftCellDropdownProps = PropsWithChildren<{
  title?: string;
  isPlacementBottom?: boolean;
  shiftData?: OMSShift;
  onShiftChange: (shiftPayload?: OMSShiftSelectPayload) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  canReset?: boolean;
  getResettableSlot?: (slotIndex: number) => boolean;
}>;

export const OMSShiftCellDropdown = ({
  title = 'Change Status',
  isPlacementBottom,
  isOpen,
  onOpenChange,
  triggerRef,
  shiftData,
  onShiftChange,
  canReset,
  getResettableSlot,
}: OMSShiftCellDropdownProps) => {
  const {
    selectedShiftKeys,
    disabledShiftKeys,
    getOptionSelectedSlotKeys,
    onSelectionChange,
    createSlotsSelectionHandler,
    getDisabledSlotKeys,
    shouldDisallowEmptySelection,
  } = useOMSShiftCellDropdown({
    shiftData,
    onShiftChange,
    onOpenChange,
    canReset,
    getResettableSlot,
  });

  return createPortal(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <Popover
      placement={isPlacementBottom ? 'bottom-start' : 'right-start'}
      // @ts-expect-error HeroUI Popover triggerRef expects RefObject<HTMLElement>, React 19 provides RefObject<HTMLButtonElement | null>
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      shouldCloseOnInteractOutside={(el) => el !== triggerRef.current && !triggerRef.current?.contains(el)}
    >
      <PopoverContent>
        <Listbox
          selectionMode="single"
          disallowEmptySelection={!canReset}
          selectedKeys={selectedShiftKeys}
          disabledKeys={disabledShiftKeys}
          onSelectionChange={onSelectionChange}
          aria-label="Change Shift Status"
          topContent={
            <>
              <span className="pl-2 text-s-m text-base-20">{title}</span>
              <Divider orientation="horizontal" />
            </>
          }
        >
          {OMS_SHIFT_OPTION_DROPDOWN_LIST.map((optionItem) => {
            const color = getShiftOptionIconColor(optionItem.value);
            const needsSlotSelection = isDualSlotOptionType(optionItem.value);

            return (
              <ListboxItem
                key={optionItem.value}
                textValue={optionItem.name}
                isReadOnly={needsSlotSelection}
                startContent={<MdTripOrigin size={16} className={color} />}
              >
                {needsSlotSelection ? (
                  <DualSlotDropdown
                    optionName={optionItem.name}
                    selectedSlotKeys={getOptionSelectedSlotKeys(optionItem.value)}
                    disabledSlotKeys={getDisabledSlotKeys(optionItem.value)}
                    disallowEmptySelection={shouldDisallowEmptySelection(optionItem.value)}
                    onSlotsSelectionChange={createSlotsSelectionHandler(optionItem.value)}
                    infoMessage={isAdminDayOption(optionItem.value) ? OMS_ADMIN_DAY_COMBINE_INFO : undefined}
                  />
                ) : (
                  optionItem.name
                )}
              </ListboxItem>
            );
          })}
        </Listbox>
      </PopoverContent>
    </Popover>,
    document.body,
  );
};
